/**
 * ForgotPasswordModal — Phase 26
 *
 * Small dialog over the login page. POSTs to /api/auth/forgot-password.
 * Backend always returns 200 (anti-enumeration), so we show a generic
 * success message regardless of whether the email is registered.
 */
import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, MailCheck } from "lucide-react";

export default function ForgotPasswordModal({ open, onOpenChange, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!email.includes("@")) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      // Backend is idempotent 200 — but even on transport error we still
      // show the generic success message to avoid enumeration.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setDone(false); }}>
      <DialogContent data-testid="forgot-password-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            Enter the email address on your RunIdea account. If we find a match, we'll send a reset link.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div data-testid="forgot-password-success" className="flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200">
            <MailCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Check your inbox.</div>
              <div className="text-emerald-800 mt-1">If <b>{email}</b> is registered, a reset link is on its way. It expires in 60 minutes.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label htmlFor="forgot-email" className="text-xs font-medium text-slate-700">Email</Label>
              <Input
                data-testid="forgot-password-email"
                id="forgot-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
          </form>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="forgot-password-close">Close</Button>
          {!done && (
            <Button
              data-testid="forgot-password-submit"
              onClick={onSubmit}
              disabled={submitting || !email.includes("@")}
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Send reset link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
